'use client'
import type { LoyaltyVoucherCardProps } from '@maestro/commerce-blocks/loyalty/components/loyalty-voucher-card/loyalty-voucher-card.types'

import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import { daysUntil } from '@maestro/commerce-blocks/utils/date.utils'
import {
  buildPrice,
  formatPriceAsText,
} from '@maestro/commerce-blocks/utils/price.utils'
import { theme } from '@maestro/local/theme'
import { Checkbox } from '@pearlconvert/ui/components/checkbox'

export const useLoyaltyVoucherCard = (props: LoyaltyVoucherCardProps) => {
  const {
    voucher,
    isDisabled,
    onSelectVoucher,
    isSelectionEnabled,
    isSelected,
  } = props

  const t = useTranslations('commerce-blocks.loyalty')

  const handleSelect = useCallback(() => {
    if (isDisabled) {
      return
    }
    onSelectVoucher?.(voucher.id)
  }, [isDisabled, onSelectVoucher, voucher.id])

  const loyaltyVoucherValue = useMemo(() => {
    switch (voucher.valueType) {
      case 'PERCENT':
        return `-${voucher.value}%`
      case 'ABSOLUTE':
        return formatPriceAsText(
          buildPrice(theme().priceConfig.currencyIso, voucher.value * 100)
        )
      default:
        return ''
    }
  }, [voucher.value, voucher.valueType])

  const loyaltyVoucherName = useMemo(() => {
    if (isSelectionEnabled) {
      return (
        <Checkbox
          id={`loyalty-voucher-${voucher.id}`}
          label={voucher.name}
          onChange={handleSelect}
          checked={isSelected}
          disabled={isDisabled}
        />
      )
    } else {
      return voucher.name
    }
  }, [
    handleSelect,
    isDisabled,
    isSelected,
    isSelectionEnabled,
    voucher.id,
    voucher.name,
  ])

  const loyaltyVoucherExpiresIn = useMemo(() => {
    if (!voucher.validTo) {
      return ''
    }

    return t('voucher.expires-in', {
      days: daysUntil(voucher.validTo),
    })
  }, [t, voucher.validTo])

  return {
    loyaltyVoucherName,
    loyaltyVoucherValue,
    loyaltyVoucherExpiresIn,
  }
}
